import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import type { CurrentUser } from "@/lib/auth";
import { getMontageunterlagen, type Montageunterlage } from "@/lib/projectOverview";
import { MontageunterlagenDialog } from "@/projectOverview/MontageunterlagenDialog";
import { userCanManageMontageunterlagen } from "@/projectOverview/permissions";
import { MyDayMontageunterlagenViewModal } from "@/pages/home/MyDayMontageunterlagenViewModal";

export function MyDayOrderMontageBlock({
  orderId,
  orderLabel,
  user,
  groups,
}: {
  orderId: number;
  orderLabel: string;
  user: CurrentUser | null;
  groups: string[];
}) {
  const [items, setItems] = useState<Montageunterlage[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getMontageunterlagen(orderId);
      setItems(Array.isArray(list) ? list : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!viewOpen && !editOpen) void reload();
  }, [viewOpen, editOpen, reload]);

  const canManage = userCanManageMontageunterlagen(user, groups);
  const notes = (items ?? []).filter((i) => i.entry_type === "note");
  const addresses = (items ?? []).filter((i) => i.entry_type === "address");
  const files = (items ?? []).filter((i) => i.entry_type === "file");
  const hasMontageData = notes.length > 0 || addresses.length > 0 || files.length > 0;

  if (loading && items === null) {
    return (
      <p className="mt-2 text-xs text-label-tertiary">Montageunterlagen werden geladen …</p>
    );
  }

  if (!hasMontageData) {
    return null;
  }

  return (
    <div className="mt-2.5 border-t border-separator pt-2">
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
          Montageunterlagen · {orderLabel}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-[11px]"
          onClick={() => setViewOpen(true)}
        >
          {files.length > 0
            ? `Dateien & Notizen (${files.length} Datei${files.length === 1 ? "" : "en"})`
            : hasMontageData
              ? "Dateien & Notizen"
              : "Montageunterlagen"}
        </Button>
      </div>

      {addresses.map((item) => (
        <div key={item.id} className="mb-2 rounded-lg border border-separator/80 bg-card/60 px-2.5 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
            {item.title?.trim() || "Adresse"}
          </div>
          {item.body_text ? (
            <p className="mt-0.5 whitespace-pre-wrap text-xs leading-relaxed text-label-secondary">
              {item.body_text}
            </p>
          ) : null}
          {item.maps_url ? (
            <a
              href={item.maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex text-xs text-accent hover:underline"
            >
              In Google Maps öffnen
            </a>
          ) : null}
        </div>
      ))}

      {notes.map((item) => (
        <div key={item.id} className="mb-2 rounded-lg border border-separator/80 bg-card/60 px-2.5 py-2">
          {item.title?.trim() ? (
            <div className="text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
              {item.title.trim()}
            </div>
          ) : (
            <div className="text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
              Notiz
            </div>
          )}
          {item.body_text ? (
            <p className="mt-0.5 whitespace-pre-wrap text-xs leading-relaxed text-label-secondary">
              {item.body_text}
            </p>
          ) : null}
        </div>
      ))}

      <MyDayMontageunterlagenViewModal
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        orderId={orderId}
        orderLabel={orderLabel}
        onEdit={
          canManage
            ? () => {
                setViewOpen(false);
                setEditOpen(true);
              }
            : undefined
        }
      />

      {canManage ? (
        <MontageunterlagenDialog
          open={editOpen}
          orderId={orderId}
          auftragsnummer={orderLabel}
          user={user}
          groups={groups}
          onClose={() => setEditOpen(false)}
        />
      ) : null}
    </div>
  );
}
